import { AdaptiveDpr, OrbitControls } from '@react-three/drei';
import { Canvas, useThree } from '@react-three/fiber';
import React, { useCallback, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import { EInteractionMode } from '../../@types/Enums';

import { useSceneData } from '../../provider/SceneDataProvider';
import { useToolbarContext } from '../../provider/ToolbarProvider';
import { CAMERA_DISTANCE, DEFAULT_ZOOM_VALUE, LIGHT_POSITION } from '../../utils/constants';
import Annotations from './Annotations';
import Building from './Building';
import ClearCanvas from './ClearCanvas';
import FloorPlannerGrid from './FloorPlannerGrid';
import CanvasRotator from './RotateCanvas';

const ZOOM_UPDATE_STEP = 0.1;

const quantizeZoom = (zoom: number) => Number((Math.round(zoom / ZOOM_UPDATE_STEP) * ZOOM_UPDATE_STEP).toFixed(2));

const SceneManager = () => {
  const { scene } = useThree();
  const { setSceneRef } = useSceneData();
  const sceneRef = useRef(scene);

  useEffect(() => {
    setSceneRef(sceneRef);
  }, [setSceneRef]);

  // This component does not render anything
  return null;
};

const FloorPlannerScene = () => {
  const controlsRef = useRef<OrbitControlsImpl>(null);
  const pendingZoomRef = useRef(DEFAULT_ZOOM_VALUE);
  const lastPublishedZoomRef = useRef(DEFAULT_ZOOM_VALUE);
  const zoomAnimationFrameRef = useRef<number | null>(null);

  const { sceneNeedsClearing, sceneRef, setZoomData } = useSceneData();
  const { interactionMode } = useToolbarContext();

  const handleZoomChange = useCallback(() => {
    if (controlsRef.current === null) {
      return;
    }

    pendingZoomRef.current = controlsRef.current.object.zoom;

    if (zoomAnimationFrameRef.current !== null) {
      return;
    }

    zoomAnimationFrameRef.current = requestAnimationFrame(() => {
      zoomAnimationFrameRef.current = null;
      const nextZoom = quantizeZoom(pendingZoomRef.current);

      if (nextZoom === lastPublishedZoomRef.current) {
        return;
      }

      lastPublishedZoomRef.current = nextZoom;
      setZoomData(nextZoom);
    });
  }, [setZoomData]);

  useEffect(
    () => () => {
      if (zoomAnimationFrameRef.current !== null) {
        cancelAnimationFrame(zoomAnimationFrameRef.current);
      }
    },
    [],
  );

  return (
    <Canvas
      flat
      style={{
        background: '#f9f9f9',
        position: 'fixed',
      }}
      gl={{
        antialias: true,
        preserveDrawingBuffer: true,
      }}
      orthographic={true}
      camera={{
        zoom: DEFAULT_ZOOM_VALUE,
        position: [0, 0, CAMERA_DISTANCE],
      }}
      performance={{ min: 0.1 }}
      frameloop="demand"
    >
      <ambientLight name={'scene-ambientLight'} color={0xffffff} intensity={1.0} />
      <pointLight name={'scene-pointLight'} position={[LIGHT_POSITION, LIGHT_POSITION, LIGHT_POSITION]} />
      <Building />
      <Annotations />
      <OrbitControls
        mouseButtons={{
          LEFT: interactionMode === EInteractionMode.MOVE ? THREE.MOUSE.PAN : undefined,
          RIGHT: THREE.MOUSE.PAN,
        }}
        ref={controlsRef}
        enableRotate={false}
        onChange={handleZoomChange}
        minZoom={1.25}
        maxZoom={60}
        enableDamping
        dampingFactor={0.08}
        zoomToCursor
      />
      <AdaptiveDpr pixelated={false} />
      <FloorPlannerGrid />
      {sceneRef.current === null && <SceneManager />}
      {sceneNeedsClearing && <ClearCanvas />}
      <CanvasRotator />
    </Canvas>
  );
};

export default FloorPlannerScene;
